import type { z } from 'zod';

import { ApiErrorEnvelopeSchema } from './schemas';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const FALLBACK_ERROR_MESSAGE = 'No pudimos completar eso ahora mismo.';

type Session = { transport: 'cookie' | 'bearer'; token?: string };
type AuthBinding = { getSession: () => Session | null; onUnauthorized: () => void };
type RequestInput = {
  method?: string;
  headers?: Record<string, string>;
} & ({ body?: unknown; multipart?: false } | { body: FormData; multipart: true });

/** Share transport mechanics while each client owns its session and 401 handler. */
export function createRequestTransport(auth: AuthBinding) {
  return async function request<TSchema extends z.ZodType>(
    path: string,
    schema: TSchema,
    input: RequestInput = {},
  ): Promise<z.infer<TSchema>> {
    const session = auth.getSession();
    const headers = {
      Accept: 'application/json',
      ...input.headers,
      ...(input.body !== undefined && !input.multipart ? { 'Content-Type': 'application/json' } : {}),
      ...(session?.transport === 'bearer' && session.token ? { Authorization: `Bearer ${session.token}` } : {}),
    };
    const response = await fetch(path, {
      method: input.method ?? 'GET',
      headers,
      // Cookie sessions and login responses require cookies before transport is known.
      credentials: 'include',
      body: input.multipart ? input.body : input.body === undefined ? undefined : JSON.stringify(input.body),
    });

    if (response.status === 204) return schema.parse(undefined);
    const payload = await readJson(response);
    if (!response.ok) {
      if (response.status === 401) auth.onUnauthorized();
      throw toApiError(response.status, payload);
    }
    return schema.parse(payload);
  };
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    // Preserve schema handling of absent payloads and generic errors for non-JSON failures.
    return undefined;
  }
}

function toApiError(status: number, payload: unknown): ApiError {
  const envelope = ApiErrorEnvelopeSchema.safeParse(payload);
  if (envelope.success) {
    const { code, message, fields } = envelope.data.error;
    return new ApiError(status, code, message, fields);
  }
  return new ApiError(status, 'system_error', FALLBACK_ERROR_MESSAGE);
}
